import { Button, Card, CardPreview, Image, Text, makeStyles, tokens } from "@fluentui/react-components";
import React, { MutableRefObject, useRef } from "react";
import { maxImageSizeInBytes } from "../../common/commonConstants";
import { deleteStationImage, invalidFileForImageUpload, uploadStationImage } from "../../reducers/diningReducer";
import { useAppDispatch } from "../../store/store";
import styles from "../../styles/stationImageList.module.css";

const acceptedImageTypes = ["image/jpg", "image/jpeg", "image/png"];

type Props = {
  name: string;
  cafeId: string;
  stationId: string;
  imageUrl?: string;
};

/* =======================
   Styles
======================= */

const useClasses = makeStyles({
  card: {
    boxShadow: tokens.shadow16,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  header: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "16px",
    paddingBottom: "0px",
  },

  preview: {
    position: "relative",
    height: "150px",
    backgroundColor: tokens.colorNeutralBackground2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  image: {
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
  },
});

/* =======================
   Component
======================= */

const StationImageCard = ({ name, cafeId, stationId, imageUrl }: Props) => {
  const classes = useClasses();
  const dispatch = useAppDispatch();

  const uploadRefs: MutableRefObject<Record<string, HTMLInputElement | null>> = useRef({});

  const uploadImage = (files: FileList | null) => {
    if (
      !files ||
      files.length === 0 ||
      files[0].size > maxImageSizeInBytes ||
      !acceptedImageTypes.includes(files[0].type)
    ) {
      dispatch(invalidFileForImageUpload());
      return;
    }

    dispatch(
      uploadStationImage(cafeId, stationId, {
        data: files[0],
        fileName: files[0].name,
      }),
    );
  };

  const deleteImage = () => {
    dispatch(deleteStationImage(cafeId, stationId));
  };

  return (
    <Card className={`${styles.card}`}>
      {/* Category */}
      <Text className={styles.cardCategory}>Station image</Text>

      {/* Header */}
      <div className={classes.header}>
        <Text weight="semibold" className={styles.cardTitle}>
          {name}
        </Text>
        <Text size={200} className={styles.cardSubtitle}>
          Station image preview
        </Text>
      </div>
      <div className={styles.imageContainer}>
        {/* Image Preview */}
        <CardPreview className={`${styles.cardImage} ${classes.preview}`}>
          <Image src={imageUrl || ""} alt={imageUrl ? name : "No image uploaded"} className={classes.image} />
        </CardPreview>
        {imageUrl && (
          <button
            className={styles.deleteImageButton}
            data-testid={"deleteButton"}
            onClick={() => deleteImage()}
            aria-label={`Remove image for station ${name}`}
          >
            <span aria-hidden={true}>&#10006;</span>
          </button>
        )}
      </div>
      {/* Actions */}
      <div className={styles.cardActions}>
        <label>
          <div className={styles.browseLabel}>Change station&apos;s image</div>

          <input
            className={styles.hidden}
            type="file"
            accept="image/jpg,image/jpeg,image/png"
            ref={(ref) => (uploadRefs.current[stationId] = ref)}
            onChange={(e) => uploadImage(e.target.files)}
          />

          <Button onClick={() => uploadRefs.current[stationId]?.click()}>Browse</Button>
        </label>
      </div>
    </Card>
  );
};

export default StationImageCard;
